import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Checkbox,
  Tooltip,
  IconButton,
  TablePagination,
  LinearProgress,
  Skeleton,
  CircularProgress
} from '@mui/material'
import {
  TravelExplore,
  PersonSearch,
  People,
  AlternateEmail,
  MailOutline,
  Download,
  CheckCircle
} from '@mui/icons-material'
import ExcelIcon from '../../../../../components/icon/ExcelIcon'
import DartIcon from '../atoms/DartIcon'
import CampaignFirmRow from './CampaignFirmRow'
import CampaignFilterToolbar from '../molecules/CampaignFilterToolbar'
import CampaignProgressBars from './CampaignActionCards'

const RELEVANCY_LEVELS = [
  { key: 'HIGHLY_SUITABLE', color: '#1B5E20', labelKey: 'mailTemplate.relevancyLevel.highlyRelevant' },
  { key: 'MODERATELY_SUITABLE', color: '#388E3C', labelKey: 'mailTemplate.relevancyLevel.moderatelyRelevant' },
  { key: 'MINIMALLY_SUITABLE', color: '#8BC34A', labelKey: 'mailTemplate.relevancyLevel.minimallyRelevant' },
  { key: 'NOT_SUITABLE', color: '#F44336', labelKey: 'mailTemplate.relevancyLevel.notRelevant' },
  { key: 'CANNOT_ACCESS', color: '#660101', labelKey: 'mailTemplate.relevancyLevel.cannotAccess' },
  { key: 'UNCHECKED', color: '#9E9E9E', labelKey: 'mailTemplate.relevancyLevel.unchecked' }
]

const StatCard = ({ label, value, sub, color, valueColor, icon: Icon, tooltip }) => (
  <Tooltip title={tooltip || ''} arrow placement="top">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: tooltip ? 'help' : 'default' }}>
      <Box sx={{
        width: 32,
        height: 32,
        borderRadius: '8px',
        backgroundColor: `${color}12`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon sx={{ fontSize: 17, color }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.375 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2, color: valueColor || color }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 500 }}>{sub}</Typography>}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>{label}</Typography>
      </Box>
    </Box>
  </Tooltip>
)

const RelevancyBar = ({ levelCounts, total, t }) => {
  if (total === 0) {
    return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <DartIcon sx={{ fontSize: 16, color: '#bbb', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.disabled', whiteSpace: 'nowrap', mb: 0.25 }}>
          {t('mailTemplate.campaigns.dashboard.stats.relevancyChecked')}
        </Typography>
        <Box sx={{ display: 'flex', height: 4, borderRadius: 2, backgroundColor: '#eee' }} />
      </Box>
    </Box>
    )
  }
  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <DartIcon sx={{ fontSize: 16, color: '#7C4DFF', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap', mb: 0.25 }}>
          {t('mailTemplate.campaigns.dashboard.stats.relevancyChecked')}
        </Typography>
        <Box sx={{ display: 'flex', mb: 0.25 }}>
          {RELEVANCY_LEVELS.map(level => {
            const count = levelCounts[level.key]
            if (count === 0) return null
            return (
              <Box key={level.key} sx={{ width: `${(count / total) * 100}%`, display: 'flex', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: level.color, lineHeight: 1 }}>{count}</Typography>
              </Box>
            )
          })}
        </Box>
        <Box sx={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', backgroundColor: '#eee' }}>
          {RELEVANCY_LEVELS.map(level => {
            const count = levelCounts[level.key]
            if (count === 0) return null
            return (
              <Tooltip key={level.key} title={`${t(level.labelKey)}: ${count}`} arrow>
                <Box sx={{ width: `${(count / total) * 100}%`, backgroundColor: level.color, transition: 'width 0.3s' }} />
              </Tooltip>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

const CampaignStatsBar = ({ statistics, totalCount }) => {
  const { t } = useTranslation()

  const data = useMemo(() => {
    const suitabilityCounts = statistics?.relevancyCheck?.suitabilityCounts || {}
    const levelCounts = {
      HIGHLY_SUITABLE: suitabilityCounts.HIGHLY_SUITABLE || 0,
      MODERATELY_SUITABLE: suitabilityCounts.MODERATELY_SUITABLE || 0,
      MINIMALLY_SUITABLE: suitabilityCounts.MINIMALLY_SUITABLE || 0,
      NOT_SUITABLE: suitabilityCounts.NOT_SUITABLE || 0,
      CANNOT_ACCESS: suitabilityCounts.CANNOT_ACCESS || 0,
      UNCHECKED: suitabilityCounts.UNCHECKED || 0
    }
    const relevancyChecked = Object.values(levelCounts).reduce((sum, v) => sum + v, 0)

    return {
      relevancyChecked,
      levelCounts,
      premiumContacts: statistics?.contactStats?.enriched ?? '-',
      genericEmails: statistics?.emailStats?.generic ?? '-',
      premiumEmails: statistics?.emailStats?.enriched ?? '-',
      totalEmails: statistics?.emailStats?.total ?? '-',
      selectedGeneric: statistics?.selectedContactStats?.generic ?? '-',
      selectedPremium: statistics?.selectedContactStats?.enriched ?? '-',
      selectedTotal: statistics?.selectedContactStats?.total ?? '-',
      contentGeneric: statistics?.emailContentStats?.generic ?? '-',
      contentPremium: statistics?.emailContentStats?.enriched ?? '-',
      contentTotal: statistics?.emailContentStats?.total ?? '-'
    }
  }, [statistics])

  if (totalCount === 0 || !statistics) return null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider', py: 1, pl: 12, pr: 5, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
      <RelevancyBar levelCounts={data.levelCounts} total={data.relevancyChecked} t={t} />
      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider' }} />
      <Box sx={{ px: 2, minWidth: 120 }}>
        <StatCard label={t('mailTemplate.campaigns.dashboard.stats.premiumContacts')} value={data.premiumContacts} color="#5C6BC0" valueColor="text.primary" icon={People} tooltip={t('mailTemplate.campaigns.dashboard.stats.premiumContactsTooltip')} />
      </Box>
      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider' }} />
      <Tooltip title={t('mailTemplate.campaigns.dashboard.stats.emailsFoundTooltip')} arrow placement="top">
        <Box sx={{ px: 2, cursor: 'help', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#E6510012', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlternateEmail sx={{ fontSize: 17, color: '#E65100' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>{data.totalEmails}</Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>{t('mailTemplate.campaigns.dashboard.stats.emailsFound')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AlternateEmail sx={{ fontSize: 14, color: '#0097A7' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0097A7' }}>{data.genericEmails}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PersonSearch sx={{ fontSize: 14, color: '#E65100' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#E65100' }}>{data.premiumEmails}</Typography>
            </Box>
          </Box>
        </Box>
      </Tooltip>
      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider' }} />
      <Tooltip title={t('mailTemplate.campaigns.dashboard.stats.selectedTooltip')} arrow placement="top">
        <Box sx={{ px: 2, cursor: 'help', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#2E7D3212', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle sx={{ fontSize: 17, color: '#2E7D32' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>{data.selectedTotal}</Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>{t('mailTemplate.campaigns.dashboard.stats.selected')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AlternateEmail sx={{ fontSize: 14, color: '#0097A7' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0097A7' }}>{data.selectedGeneric}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PersonSearch sx={{ fontSize: 14, color: '#E65100' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#E65100' }}>{data.selectedPremium}</Typography>
            </Box>
          </Box>
        </Box>
      </Tooltip>
      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider' }} />
      <Tooltip title={t('mailTemplate.campaigns.dashboard.stats.contentReadyTooltip')} arrow placement="top">
        <Box sx={{ px: 2, cursor: 'help', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#2E7D3212', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MailOutline sx={{ fontSize: 17, color: '#2E7D32' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>{data.contentTotal}</Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>{t('mailTemplate.campaigns.dashboard.stats.contentReady')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AlternateEmail sx={{ fontSize: 14, color: '#0097A7' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0097A7' }}>{data.contentGeneric}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PersonSearch sx={{ fontSize: 14, color: '#E65100' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#E65100' }}>{data.contentPremium}</Typography>
            </Box>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  )
}

const TOUR_EXPAND_STEPS = [3, 5, 6, 7]

const BULK_ACTIONS = [
  { key: 'checkRelevancy', taskType: 'relevancy', icon: DartIcon, labelKey: 'mailTemplate.campaigns.dashboard.relevancyCheck', color: '#7C4DFF' },
  { key: 'searchContacts', taskType: 'contacts', icon: PersonSearch, labelKey: 'mailTemplate.campaigns.dashboard.contactSearch', color: '#0097A7' },
  { key: 'queryEmailAddresses', taskType: 'emailQuery', icon: AlternateEmail, labelKey: 'mailTemplate.campaigns.dashboard.emailAddressQuery', color: '#E65100' },
  { key: 'generateContent', taskType: 'content', icon: MailOutline, labelKey: 'mailTemplate.campaigns.dashboard.generateContent', color: '#2E7D32' }
]

const SelectionActions = ({ selectedCount, onBulkAction, disabled = false, disabledTooltip, tasks = [] }) => {
  const { t } = useTranslation()
  const detail = selectedCount > 0
    ? t('mailTemplate.campaigns.dashboard.firmsCount', { count: selectedCount })
    : t('mailTemplate.campaigns.dashboard.allFirms')

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
      {BULK_ACTIONS.map(({ key, taskType, icon: Icon, labelKey, color }) => {
        const label = t(labelKey)
        const isLoading = tasks.some(t => t.type === taskType && t.loading)
        const isDisabled = disabled || isLoading
        const tooltip = disabled
          ? disabledTooltip
          : `${label} (${detail})`
        return (
          <Tooltip key={key} title={tooltip} arrow placement="top">
            <Box
              data-tour={`campaign-action-${key}`}
              onClick={() => !isDisabled && onBulkAction?.(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.625,
                borderRadius: '8px',
                cursor: isDisabled ? 'default' : 'pointer',
                color: isDisabled ? `${color}60` : color,
                backgroundColor: isDisabled ? `${color}08` : `${color}10`,
                border: '1px solid',
                borderColor: isDisabled ? `${color}15` : `${color}25`,
                transition: 'all 0.15s',
                ...(!isDisabled && {
                  '&:hover': {
                    backgroundColor: `${color}22`,
                    borderColor: `${color}50`
                  }
                })
              }}
            >
              {isLoading
                ? <CircularProgress size={18} sx={{ color }} />
                : <Icon sx={{ fontSize: 18 }} />
              }
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'block' } }}>
                {label}
              </Typography>
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

const LoadingSkeleton = () => (
  <Box>
    {Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
      <Box key={i} sx={{ px: 2, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="rounded" width={20} height={20} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={22} />
            <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
)

const EmptyState = () => {
  const { t } = useTranslation()
  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <TravelExplore sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body1" color="text.secondary">
        {t('mailTemplate.campaigns.dashboard.noFirmsMatch')}
      </Typography>
    </Box>
  )
}

const CampaignDashboardTable = ({
  firms = [],
  loading = false,
  statistics,
  selectedFirmIds = {},
  selectAllMode,
  excludedCount = 0,
  onToggleFirmSelect,
  onBulkAction,
  onContactToggle,
  onPremiumContactToggle,
  page = 0,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  tasks,
  onEmailPreview,
  onSelectAllItems,
  onClearSelection,
  filters,
  onFilterChange,
  sorting,
  onSortChange,
  onDeleteFirm,
  onDownloadExcel,
  excelLoading = false,
  tourStepIndex
}) => {
  const { t } = useTranslation()

  const [expandedFirmId, setExpandedFirmId] = useState(null)
  const handleToggleExpand = useCallback(
    (firmId) => {
      setExpandedFirmId((prev) => (prev === firmId ? null : firmId))
    },
    []
  )

  useEffect(() => {
    if (TOUR_EXPAND_STEPS.includes(tourStepIndex) && firms.length > 0) {
      setExpandedFirmId(firms[0].id)
    } else if (tourStepIndex >= 0 && !TOUR_EXPAND_STEPS.includes(tourStepIndex)) {
      setExpandedFirmId(null)
    }
  }, [tourStepIndex, firms])

  const allPageSelected = useMemo(() => {
    if (!firms.length) return false
    return firms.every((f) => selectedFirmIds[f.id])
  }, [firms, selectedFirmIds])

  const allSelected = selectAllMode === 'all' || allPageSelected

  const handleToggleAll = useCallback(() => {
    if (!firms.length) return
    if (allSelected) {
      if (onClearSelection) onClearSelection()
    } else {
      firms.forEach((f) => onToggleFirmSelect?.(f.id, true))
    }
  }, [firms, allSelected, onToggleFirmSelect, onClearSelection])

  const pageSelectedCount = useMemo(() => {
    return firms.filter((f) => selectedFirmIds[f.id]).length
  }, [firms, selectedFirmIds])

  const someSelected = selectAllMode !== 'all' && pageSelectedCount > 0 && pageSelectedCount < firms.length

  const totalSelectedCount = useMemo(() => {
    return Object.values(selectedFirmIds).filter(Boolean).length
  }, [selectedFirmIds])

  const selectedCount = useMemo(() => {
    if (selectAllMode === 'all') return totalCount
    if (selectAllMode === 'allExcept') return totalCount - excludedCount
    return totalSelectedCount
  }, [selectAllMode, totalCount, excludedCount, totalSelectedCount])

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'background.paper'
        }}
      >
      <CampaignFilterToolbar filters={filters} onFilterChange={onFilterChange} sorting={sorting} onSortChange={onSortChange} />
      <CampaignStatsBar statistics={statistics} totalCount={totalCount} />
      <CampaignProgressBars tasks={tasks} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(0, 0, 0, 0.02)'
        }}
      >
        <Box sx={{ width: 32 }} />
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          sx={{ p: 0.5, flexShrink: 0 }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: 'text.secondary'
          }}
        >
          {t('mailTemplate.campaigns.dashboard.firmsAndContacts')}
        </Typography>
        <SelectionActions
          selectedCount={selectedCount}
          onBulkAction={onBulkAction}
          disabled={totalCount === 0 || selectedCount === 0}
          disabledTooltip={totalCount === 0
            ? t('mailTemplate.campaigns.dashboard.noFirmsToProcess')
            : t('mailTemplate.campaigns.dashboard.selectFirmsFirst')}
          tasks={tasks}
        />
        <Tooltip title={totalCount === 0 ? t('mailTemplate.campaigns.dashboard.noFirmsToProcess') : t('mailTemplate.campaigns.dashboard.downloadExcel')} arrow placement="top">
          <span>
            <IconButton
              size="small"
              disabled={totalCount === 0 || excelLoading}
              onClick={onDownloadExcel}
              sx={{
                p: 0.5,
                borderRadius: '8px',
                color: '#1B6B34',
                '&:hover': { color: '#145226', backgroundColor: 'rgba(27, 107, 52, 0.08)' }
              }}
            >
              {excelLoading && <CircularProgress size={14} sx={{ color: '#1B6B34' }} />}
              <ExcelIcon width={18} height={18} color="currentColor" />
              <Download sx={{ fontSize: 14, ml: -0.25 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {allPageSelected && totalCount > firms.length && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 0.75,
            backgroundColor: 'rgba(25, 118, 210, 0.06)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontSize: '0.8rem'
          }}
        >
          {selectAllMode === 'all'
            ? (
              <>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {t('mailTemplate.campaigns.dashboard.allFirmsSelected', { count: totalCount })}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={onClearSelection}
                  sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                >
                  {t('mailTemplate.campaigns.dashboard.clearSelection')}
                </Typography>
              </>
              )
            : (
              <>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {t('mailTemplate.campaigns.dashboard.allPageSelected', { count: firms.length })}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={onSelectAllItems}
                  sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                >
                  {t('mailTemplate.campaigns.dashboard.selectAllFirms', { count: totalCount })}
                </Typography>
              </>
              )
          }
        </Box>
      )}

      {loading && <LinearProgress sx={{ height: 2 }} />}

      {loading
        ? <LoadingSkeleton />
        : firms.length === 0
          ? <EmptyState />
          : firms.map((firm, index) => (
            <CampaignFirmRow
              key={firm.id}
              firm={firm}
              isFirstRow={index === 0}
              isExpanded={expandedFirmId === firm.id}
              isSelected={Boolean(selectedFirmIds[firm.id])}
              onToggleExpand={() => handleToggleExpand(firm.id)}
              onToggleSelect={() => onToggleFirmSelect?.(firm.id)}
              onContactToggle={onContactToggle}
              onPremiumContactToggle={onPremiumContactToggle}
              onEmailPreview={onEmailPreview}
              onDelete={onDeleteFirm}
            />
          ))
      }

      {!loading && firms.length > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('mailTemplate.campaigns.dashboard.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('mailTemplate.campaigns.dashboard.displayedRows', { from, to, count: count !== -1 ? count : `>${to}` })}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': {
              minHeight: 48,
              px: 2,
              display: 'flex',
              alignItems: 'center'
            },
            '.MuiTablePagination-spacer': {
              display: 'none'
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.8125rem',
              m: 0
            },
            '.MuiTablePagination-displayedRows': {
              ml: 'auto'
            },
            '.MuiTablePagination-select': {
              fontSize: '0.8125rem'
            }
          }}
        />
      )}

      </Box>
    </Box>
  )
}

export default CampaignDashboardTable
